import { AlertTriangle, BellRing, CheckCircle2, Info } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import type { BusinessAlertItem, BusinessAlerts } from '../../types/businessMetrics';
import {
  EMPTY_VALUE_LABEL,
  formatBusinessCount,
  formatBusinessDateTime,
  sortAlertItems,
} from '../../types/businessMetricsView';
import { BusinessPanel, StatGrid, StatTile } from './businessPanelParts';

export const NO_ALERTS_LABEL = '경보 없음';

const ALERT_TONE: Record<BusinessAlertItem['level'], string> = {
  critical: 'border-destructive/50 bg-destructive/10 text-destructive',
  warning: 'border-amber-300 bg-amber-50 text-amber-950',
  info: 'border-border bg-muted/40 text-foreground',
};

const ALERT_LEVEL_LABEL: Record<BusinessAlertItem['level'], string> = {
  critical: '긴급',
  warning: '주의',
  info: '안내',
};

/** 섹션 맨 위 경보 띠. critical 은 role="alert", 나머지는 role="status". */
export function BusinessAlertBand({ items }: { items: BusinessAlertItem[] }) {
  if (items.length === 0) {
    return (
      <div
        role="status"
        data-testid="business-alert-band"
        className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
      >
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        {NO_ALERTS_LABEL}
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="business-alert-band" aria-label="사업 지표 경보">
      {sortAlertItems(items).map((item, index) => {
        const Icon = item.level === 'info' ? Info : AlertTriangle;
        return (
          <li
            key={`${item.code}-${index}`}
            role={item.level === 'critical' ? 'alert' : 'status'}
            data-alert-level={item.level}
            className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${ALERT_TONE[item.level]}`}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 break-words">{item.message}</span>
            <span className="shrink-0 text-xs font-semibold">{ALERT_LEVEL_LABEL[item.level]}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function OpsAlertsPanel({ alerts }: { alerts: BusinessAlerts }) {
  const avg7d = alerts.paymentFailuresAvg7d === null ? EMPTY_VALUE_LABEL : formatBusinessCount(alerts.paymentFailuresAvg7d);
  return (
    <BusinessPanel
      title="운영 경보"
      testId="business-alerts-panel"
      icon={BellRing}
      badge={alerts.paymentFailureSpike ? <Badge variant="destructive">결제 실패 급증</Badge> : null}
    >
      <div className="space-y-3">
        <StatGrid>
          <StatTile
            label="미해결 웹훅 실패"
            value={formatBusinessCount(alerts.webhookFailuresUnresolved)}
            tone={(alerts.webhookFailuresUnresolved ?? 0) > 0 ? 'negative' : 'default'}
          />
          <StatTile label="웹훅 실패 (24시간)" value={formatBusinessCount(alerts.webhookFailures24h)} />
          <StatTile
            label="오늘 결제 실패"
            value={formatBusinessCount(alerts.paymentFailuresToday)}
            hint={`7일 평균 ${avg7d}`}
            tone={alerts.paymentFailureSpike ? 'negative' : 'default'}
          />
          <StatTile
            label="환불 요청 대기"
            value={formatBusinessCount(alerts.refundRequestsPending)}
            tone={alerts.refundRequestsPending > 0 ? 'warning' : 'default'}
          />
          <StatTile
            label="Paddle 환불 승인 대기"
            value={formatBusinessCount(alerts.paddleRefundsPendingApproval)}
            tone={(alerts.paddleRefundsPendingApproval ?? 0) > 0 ? 'warning' : 'default'}
          />
        </StatGrid>
        {alerts.recentWebhookFailures.length === 0 ? (
          <p className="text-sm text-muted-foreground">최근 웹훅 실패가 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table aria-label="최근 웹훅 실패" className="min-w-[620px]">
              <TableHeader>
                <TableRow>
                  <TableHead>이벤트</TableHead>
                  <TableHead className="text-right">실패 횟수</TableHead>
                  <TableHead>마지막 실패</TableHead>
                  <TableHead>오류</TableHead>
                  <TableHead>상태</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.recentWebhookFailures.map((failure) => (
                  <TableRow key={failure.eventId}>
                    <TableCell>
                      <span className="font-medium">{failure.eventType}</span>
                      <p className="break-all text-xs text-muted-foreground">{failure.eventId}</p>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatBusinessCount(failure.failureCount)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{formatBusinessDateTime(failure.lastFailedAt)}</TableCell>
                    <TableCell className="max-w-[16rem] break-words text-xs">{failure.lastError ?? EMPTY_VALUE_LABEL}</TableCell>
                    <TableCell>
                      <Badge variant={failure.resolved ? 'success' : 'destructive'}>{failure.resolved ? '해결' : '미해결'}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </BusinessPanel>
  );
}
